import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Card } from 'react-bootstrap';
import CopyToClipboard from 'react-copy-to-clipboard';
import classnames from 'classnames';

import './OrganisationTools.scss';
import { CustomAccordionHeader } from '../tourDetails/common/components/CustomAccordionHeader';
import { useGetCurrentOrganization } from '../../features/organizations/organizationsSelectors';
import { getAllTours } from '../../apis/tours';
import { fetchTournamentsData } from '../../apis/tournament';
import { RaqtPopup } from '../../components/popup/RaqtPopup';
import { Tour } from '../../types/tournament';
import { TournamentsState } from '../../features/tournaments/tournamentsSlice';
import { ChainIcon } from '../../assets/icons/ChainIcon';
import { OpenInNewTabIcon } from '../../assets/icons/OpenInNewTabIcon';
import { useFindByTour } from '../../generated/orval/admin';

export const OrganisationTools = () => {
  const prefix = 'communityTools-';
  const { t } = useTranslation();
  const currentOrganization = useGetCurrentOrganization();

  const [tours, setTours] = useState<Tour[]>([]);
  const [tournaments, setTournaments] = useState<TournamentsState[]>([]);
  const [selectedTour, setSelectedTour] = useState<Tour | null>(null);
  const [selectedTournamentForLivefeed, setSelectedTournamentForLivefeed] =
    useState<TournamentsState | null>(null);
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>('light');
  const [embedLanguage, setEmbedLanguage] = useState('en');
  // Tool 1: Tour livescore embed states
  const [livefeedTheme, setLivefeedTheme] = useState<'light' | 'dark'>('light');
  const [livefeedLanguage, setLivefeedLanguage] = useState('');
  const [selectedLeaderboardId, setSelectedLeaderboardId] = useState<number | null>(null);

  const { data: tourLeaderboards } = useFindByTour(selectedTour?.id?.toString() ?? '', {
    query: { enabled: !!selectedTour?.id },
  });
  const sortedLeaderboards = [...(tourLeaderboards ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  useEffect(() => {
    if (currentOrganization?.id) {
      loadTours();
      loadTournaments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id]);

  const loadTours = async () => {
    try {
      const allTours = await getAllTours();
      setTours(allTours);
    } catch (error) {
      console.error('Failed to load tours:', error);
    }
  };

  const loadTournaments = async () => {
    if (!currentOrganization?.id) return;
    try {
      const orgTournaments = await fetchTournamentsData(currentOrganization.id);
      setTournaments(orgTournaments);
    } catch (error) {
      console.error('Failed to load tournaments:', error);
    }
  };

  const handleCopy = () => {
    // Copy handled by CopyToClipboard component
  };

  // Tool 1: Tour livescore embed
  const getTourLivescoreSrc = (tour: Tour | null) => {
    if (!tour) return '';
    const langPrefix = livefeedLanguage ? `/${livefeedLanguage}` : '';
    const leaderboardSuffix = selectedLeaderboardId
      ? `&leaderboardId=${selectedLeaderboardId}`
      : '';
    return `https://raqt.com${langPrefix}/tour/${tour.id}?embed=true&theme=${livefeedTheme}${leaderboardSuffix}`;
  };

  // Tool 2: Website embed with org lock
  const getWebsiteEmbedSrc = () => {
    if (!currentOrganization?.id) return '';
    return `https://raqt.com/${embedLanguage}/?embed=true&organizationId=${currentOrganization.id}&theme=${themeMode}`;
  };

  // Tool 3: Tournament livefeed link
  const getTournamentLivefeedLink = (tournament: TournamentsState | null) => {
    if (!tournament || !currentOrganization?.id) return '';
    return `https://raqt.com/tournament/${tournament.id}/livescore`;
  };

  if (!currentOrganization) {
    return (
      <div className="organisation-tools">
        <div className="organisation-tools__title">{t(prefix + 'title')}</div>
        <div className="organisation-tools__error">Organization not found</div>
      </div>
    );
  }

  return (
    <div className="organisation-tools">
      <div className="organisation-tools__title">{t(prefix + 'title')}</div>

      <Accordion>
        {/* Tool 1: Embed Tour Livescore Iframe */}
        <Card>
          <Card.Header className="p-0 bg-white border-0 relative">
            <CustomAccordionHeader eventKey="tourLivescore">
              {t(prefix + 'tourLeaderboardsTitle')}
            </CustomAccordionHeader>
          </Card.Header>
          <Accordion.Collapse eventKey="tourLivescore">
            <Card.Body className="organisation-tools__tool-body">
              <div className="organisation-tools__description">
                {t(prefix + 'tourLeaderboardsDescription')}
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">{t(prefix + 'selectTour')}</label>
                <select
                  className="organisation-tools__select"
                  value={selectedTour?.id || ''}
                  onChange={(e) => {
                    const tour = tours.find((t) => t.id === Number(e.target.value));
                    setSelectedTour(tour || null);
                    setSelectedLeaderboardId(null);
                  }}
                >
                  <option value="">{t(prefix + 'selectTourPlaceholder')}</option>
                  {tours.map((tour) => (
                    <option key={tour.id} value={tour.id}>
                      {tour.description || tour.name || `Tour ${tour.id}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">
                  {t(prefix + 'selectLeaderboard')}
                </label>
                <select
                  className="organisation-tools__select"
                  value={selectedLeaderboardId ?? ''}
                  disabled={!selectedTour}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedLeaderboardId(value ? Number(value) : null);
                  }}
                >
                  <option value="">{t(prefix + 'selectLeaderboardPlaceholder')}</option>
                  {sortedLeaderboards.map((leaderboard) => (
                    <option key={leaderboard.id} value={leaderboard.id}>
                      {leaderboard.name}
                    </option>
                  ))}
                </select>
                <div className="organisation-tools__helper-text">
                  {t(prefix + 'selectLeaderboardHelper')}
                </div>
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">{t(prefix + 'selectTheme')}</label>
                <div className="organisation-tools__theme-toggle">
                  <button
                    type="button"
                    className={classnames('organisation-tools__theme-button', {
                      'organisation-tools__theme-button--active': livefeedTheme === 'light',
                    })}
                    aria-pressed={livefeedTheme === 'light'}
                    onClick={() => setLivefeedTheme('light')}
                  >
                    {t(prefix + 'lightMode')}
                  </button>
                  <button
                    type="button"
                    className={classnames('organisation-tools__theme-button', {
                      'organisation-tools__theme-button--active': livefeedTheme === 'dark',
                    })}
                    aria-pressed={livefeedTheme === 'dark'}
                    onClick={() => setLivefeedTheme('dark')}
                  >
                    {t(prefix + 'darkMode')}
                  </button>
                </div>
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">{t(prefix + 'selectLanguage')}</label>
                <select
                  className="organisation-tools__select"
                  value={livefeedLanguage}
                  onChange={(e) => setLivefeedLanguage(e.target.value)}
                >
                  <option value="">{t(prefix + 'systemDefault')}</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="sv">Svenska</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                  <option value="pt">Português</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>

              {selectedTour && (
                <>
                  <div className="organisation-tools__section">
                    <label className="organisation-tools__label">{t(prefix + 'preview')}</label>
                    <div className="organisation-tools__preview">
                      <iframe
                        src={getTourLivescoreSrc(selectedTour)}
                        width="100%"
                        height="800"
                        frameBorder="0"
                        title={t(prefix + 'tourLeaderboardsTitle')}
                        className="organisation-tools__preview-iframe"
                      />
                    </div>
                  </div>

                  <div className="organisation-tools__section">
                    <label className="organisation-tools__label">{t(prefix + 'iframeCode')}</label>
                    <div className="organisation-tools__code-block">
                      <code>{`<iframe src="${getTourLivescoreSrc(
                        selectedTour
                      )}" width="100%" height="800" frameborder="0"></iframe>`}</code>
                      <div className="organisation-tools__code-block-actions">
                        <RaqtPopup
                          popupStyle="dark"
                          content={t('popup-copied')}
                          position="top center"
                        >
                          <CopyToClipboard
                            text={`<iframe src="${getTourLivescoreSrc(
                              selectedTour
                            )}" width="100%" height="800" frameborder="0"></iframe>`}
                            onCopy={handleCopy}
                          >
                            <button
                              type="button"
                              className="organisation-tools__copy-icon-button"
                              aria-label="Copy code"
                            >
                              <ChainIcon s="#666" h="20" w="20" />
                            </button>
                          </CopyToClipboard>
                        </RaqtPopup>
                        <button
                          type="button"
                          className="organisation-tools__open-icon-button"
                          aria-label="Open in new tab"
                          onClick={() => {
                            window.open(
                              getTourLivescoreSrc(selectedTour),
                              '_blank',
                              'noopener,noreferrer'
                            );
                          }}
                        >
                          <OpenInNewTabIcon w="20" h="20" s="#666" />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* Tool 2: Embed Website with Organization Lock */}
        <Card>
          <Card.Header className="p-0 bg-white border-0 relative">
            <CustomAccordionHeader eventKey="websiteEmbed">
              {t(prefix + 'websiteEmbedTitle')}
            </CustomAccordionHeader>
          </Card.Header>
          <Accordion.Collapse eventKey="websiteEmbed">
            <Card.Body className="organisation-tools__tool-body">
              <div className="organisation-tools__description">
                {t(prefix + 'websiteEmbedDescription')}
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">{t(prefix + 'selectTheme')}</label>
                <div className="organisation-tools__theme-toggle">
                  <button
                    type="button"
                    className={classnames('organisation-tools__theme-button', {
                      'organisation-tools__theme-button--active': themeMode === 'light',
                    })}
                    aria-pressed={themeMode === 'light'}
                    onClick={() => setThemeMode('light')}
                  >
                    {t(prefix + 'lightMode')}
                  </button>
                  <button
                    type="button"
                    className={classnames('organisation-tools__theme-button', {
                      'organisation-tools__theme-button--active': themeMode === 'dark',
                    })}
                    aria-pressed={themeMode === 'dark'}
                    onClick={() => setThemeMode('dark')}
                  >
                    {t(prefix + 'darkMode')}
                  </button>
                </div>
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">{t(prefix + 'selectLanguage')}</label>
                <select
                  className="organisation-tools__select"
                  value={embedLanguage}
                  onChange={(e) => setEmbedLanguage(e.target.value)}
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="sv">Svenska</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                  <option value="pt">Português</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">{t(prefix + 'preview')}</label>
                <div className="organisation-tools__preview">
                  <iframe
                    src={getWebsiteEmbedSrc()}
                    width="100%"
                    height="800"
                    frameBorder="0"
                    title="Website embed preview"
                    className="organisation-tools__preview-iframe"
                  />
                </div>
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">{t(prefix + 'iframeCode')}</label>
                <div className="organisation-tools__code-block">
                  <code>{`<iframe src="${getWebsiteEmbedSrc()}" width="100%" height="800" frameborder="0"></iframe>`}</code>
                  <RaqtPopup popupStyle="dark" content={t('popup-copied')} position="top center">
                    <CopyToClipboard
                      text={`<iframe src="${getWebsiteEmbedSrc()}" width="100%" height="800" frameborder="0"></iframe>`}
                      onCopy={handleCopy}
                    >
                      <button
                        type="button"
                        className="organisation-tools__copy-icon-button"
                        aria-label="Copy code"
                      >
                        <ChainIcon s="#666" h="20" w="20" />
                      </button>
                    </CopyToClipboard>
                  </RaqtPopup>
                </div>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* Tool 3: Tournament Livefeed Fullscreen */}
        <Card>
          <Card.Header className="p-0 bg-white border-0 relative">
            <CustomAccordionHeader eventKey="tournamentLivefeed">
              {t(prefix + 'tournamentLivefeedTitle')}
            </CustomAccordionHeader>
          </Card.Header>
          <Accordion.Collapse eventKey="tournamentLivefeed">
            <Card.Body className="organisation-tools__tool-body">
              <div className="organisation-tools__description">
                {t(prefix + 'tournamentLivefeedDescription')}
              </div>
              <div className="organisation-tools__description organisation-tools__description--secondary">
                {t(prefix + 'tournamentLivefeedTip')}
              </div>
              <div className="organisation-tools__tip">
                {t(prefix + 'tournamentLivefeedDescription2')}
              </div>

              <div className="organisation-tools__section">
                <label className="organisation-tools__label">
                  {t(prefix + 'selectTournament')}
                </label>
                <select
                  className="organisation-tools__select"
                  value={selectedTournamentForLivefeed?.id || ''}
                  onChange={(e) => {
                    const tournament = tournaments.find((t) => t.id === Number(e.target.value));
                    setSelectedTournamentForLivefeed(tournament || null);
                  }}
                >
                  <option value="">{t(prefix + 'selectTournamentPlaceholder')}</option>
                  {tournaments.map((tournament) => (
                    <option key={tournament.id} value={tournament.id}>
                      {tournament.name || `Tournament ${tournament.id}`}
                    </option>
                  ))}
                </select>
              </div>

              {selectedTournamentForLivefeed && (
                <>
                  {/* <div className="organisation-tools__section">
                    <label className="organisation-tools__label">{t(prefix + 'preview')}</label>
                    <div className="organisation-tools__preview">
                      <iframe
                        src={getTournamentLivefeedLink(selectedTournamentForLivefeed)}
                        width="100%"
                        height="800"
                        frameBorder="0"
                        title="Tournament livefeed preview"
                        className="organisation-tools__preview-iframe"
                        allow="fullscreen"
                      />
                    </div>
                  </div> */}

                  <div className="organisation-tools__section">
                    <label className="organisation-tools__label">
                      {t(prefix + 'livefeedLink')}
                    </label>
                    <div className="organisation-tools__code-block">
                      <code>{getTournamentLivefeedLink(selectedTournamentForLivefeed)}</code>
                      <div className="organisation-tools__code-block-actions">
                        <RaqtPopup
                          popupStyle="dark"
                          content={t('popup-copied')}
                          position="top center"
                        >
                          <CopyToClipboard
                            text={getTournamentLivefeedLink(selectedTournamentForLivefeed)}
                            onCopy={handleCopy}
                          >
                            <button
                              type="button"
                              className="organisation-tools__copy-icon-button"
                              aria-label="Copy link"
                            >
                              <ChainIcon s="#666" h="20" w="20" />
                            </button>
                          </CopyToClipboard>
                        </RaqtPopup>
                        <button
                          type="button"
                          className="organisation-tools__open-icon-button"
                          aria-label="Open in new tab"
                          onClick={() => {
                            window.open(
                              getTournamentLivefeedLink(selectedTournamentForLivefeed),
                              '_blank',
                              'noopener,noreferrer'
                            );
                          }}
                        >
                          <OpenInNewTabIcon w="20" h="20" s="#666" />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </div>
  );
};
